import { Messages, Token } from "../../model/Message";
import { InfoBoard } from "../../components/infoBoard/InfoBoard";
import { convertTimeToString, convertDateTimeToJapaneseString, convertDateToJapaneseString } from "../../utility/ConvertUtily";
import Badge from "../../components/badges/Badges";
import { ReplyButton } from "../../model/ReplyButton";
import { OnlineUrls } from "../../model/OnlineUrls";
import { Company } from "../../model/Companies";
import { Groups } from "../../model/Groups";
import { User } from "../../model/Users";
import { Questionnaire } from "../../model/Questionnaire";
import { useEffect, useRef, useState } from "react";

export type MessageProps = {
  value: Messages;
  replyButton?: ReplyButton;
  questionnaire?: Questionnaire;
  pointFlg?: boolean;
  onlineUrlList?: OnlineUrls[];
  col?: 'col1' | 'col2' | 'col3';
  company: Company;
  groupList: Groups[];
  userList: User[];
  tokenList: Token[];
};

export const MessageRef = ({
  value, replyButton, questionnaire, pointFlg, onlineUrlList, col = 'col1',
  company, groupList, tokenList, userList,
}: MessageProps) => {

  // 初期サイズ
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{height: number}>({height: 220});
  const [resizing, setResizing] = useState(false);

  useEffect(() => {
    const handle = containerRef.current?.querySelector(".resize-handle");

    if (handle) {
      const onMouseDown = (e: MouseEvent) => {
        e.preventDefault();
        setResizing(true);
      };

      // 型をちゃんと Element にして、イベントも合うように cast
      handle.addEventListener("mousedown", onMouseDown as EventListener);

      const handleMouseMove = (e: MouseEvent) => {
        if (resizing && containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect();
          const newHeight = e.clientY - rect.top;
          setSize({
            height: Math.max(newHeight, 200),
          });
        }
      };

      const stopResize = () => {
        setResizing(false);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", stopResize);

      return () => {
        handle.removeEventListener("mousedown", onMouseDown as EventListener);
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", stopResize);
      };
    }
  }, [resizing]);

  const userMap = new Map(userList?.map(user => [user.id, user]));
  const messageTokenList = tokenList.filter((token) => value.id === token.messageId);
  let tokenUserList = messageTokenList
  .map(token => userMap.get(token.userId))
  .filter(user => user !== undefined)
  .sort((a, b) => Number(a.id) - Number(b.id));
  if (value.select === 'topic') {
    tokenUserList = [];
  }

  const onelineUrl = onlineUrlList?.filter((item) => item.url === value?.link).at(0);

  // 配信状態の切り替え
  let publishState = (<Badge color="green">配信済み</Badge>);
  if (value.deliveredFlg) {
    publishState = (<Badge color="green">配信済み</Badge>);
  } else if (value.publishEndAt !== undefined && value.publishEndAt <= new Date()) {
    publishState = (<Badge color="gray">掲載終了</Badge>);
  } else {
    publishState = (<Badge color="red">未配信</Badge>);
  };
  if (value.draftFlg) {
    publishState = (<Badge color="blue">下書き</Badge>);
  };

  // 掲載終了日
  if (value?.publishEndAt) value.publishEndAt = new Date(value?.publishEndAt)
  const publishEndDate = Number.isNaN(value?.publishEndAt?.getTime())
    ? undefined
    : value?.publishEndAt;

  const formatQuestionnaire = (questionnaire: Questionnaire) => {
    const answerSections = [
      { title: questionnaire.title1, answers: questionnaire.answers1 },
      { title: questionnaire.title2, answers: questionnaire.answers2 },
      { title: questionnaire.title3, answers: questionnaire.answers3 },
    ];

    return answerSections
      .filter((section, index) => section.title && index < questionnaire.questionNumber)
      .map((section, index) => {
        const answers = section.answers
          .filter(answer => answer)
          .join(" / ");
        return `【質問 ${index + 1}】\n　${section.title}\n　${answers}`;
      })
      .join("\n");
  };

  let file: { [key: string]: any } = {
    '　　　　配信状態': publishState,
    '　　　　　配信先': (
        <div className="flex flex-wrap">
          {value?.topic?.map((groupId, index) => {
            let group: { deleteFlg: boolean, name: string } | undefined;
            if (groupId === '') {
              return (<span key={`group-${groupId}-${index}`}></span>);
            };
            if (groupId === "all" || groupId === company?.displayId) {
              group = { deleteFlg: false, name: "全体" };
            } else {
              const filterdGroupList = groupList?.filter((groupF) => groupF.id === groupId);
              group = filterdGroupList.length > 0
                ? { deleteFlg: filterdGroupList.at(0)?.deleteFlg ?? true, name: filterdGroupList.at(0)?.name ?? '' }
                : { deleteFlg: true, name: `削除済み: ${groupId}` };
            }
            return group && (
              <Badge
                key={`group-${groupId}-${index}`}
                color={group.deleteFlg ? "gray" : group.name !== '全体' ? "indigo" : "green"}
              >
                {group.name !== '全体' ? 'G: ' : ''}{group.name}
              </Badge>
            )
          })
          }
          {tokenUserList.map((user, index) => {
            return (
              <Badge key={`token-${user?.lastName}-${index}`} color="yellow">個別: {user?.lastName} {user?.firstName}</Badge>
            )
          })}
        </div>
      ),
  };

  if(value){
    if(value?.eventFlg){
      file = {
        ...file,
        'イベント開催日時': `${convertDateToJapaneseString(new Date(value?.eventAt))} ${convertTimeToString(new Date(value?.eventAt))}～${convertTimeToString(new Date(value?.eventEndAt))}`,
        '　　　　　　会場': value?.eventVenue,
      };
    }
    file = {
      ...file,
      '　　　　　　本文': value?.body,
    };
    if (value?.image) {
      file = {
        ...file,
        '　　　　　　画像': (<img src={value?.image} alt={'添付画像'} className={"max-h-[16rem] max-w-[16rem]"} />),
      };
    };
    file = {
      ...file,
      '　　　URLリンク': value?.urlButtonFlg ? `${value?.urlButtonName}: ${value?.urlLink}` : 'なし',
      '　　問い合わせ先': value?.contactInformation,
      '　　　返信ボタン': replyButton?.id !== "undefined" && replyButton !== undefined
      ? `${replyButton?.name}: ${replyButton?.buttonName.join(" / ")}`
      : "なし",
    };
    if(value?.eventFlg){
      let answers = "";
      let target = "";
      if (questionnaire) {
        answers = formatQuestionnaire(questionnaire);
        target = value?.replyButton === '予定表の出欠' && questionnaire.target === '全員' ? (
          "会場参加者"
        ) : value?.replyButton === '予定表の出欠(オンライン)' && questionnaire.target === '全員' ? (
          "会場・オンライン参加者"
        ) : questionnaire.target === 'オンライン参加のみ' ? (
          "オンライン参加者"
        ) : '会場参加者';
      }
      file = {
        ...file,
        'ミーティングURL': onelineUrl?.id === "none" || onelineUrl?.url === ""
        ? "設定なし"
        : onelineUrl !== undefined
          ? onelineUrl.name
          : `編集済みのURL: ${value?.link}`,
        '　イベントタイプ': value?.eventType,
      };
      if (pointFlg) {
        file = {
          ...file,
          '　　ポイント設定': value.pointSettingFlg
            ? `ポイント名：${value.pointName}\nポイントタイプ：${value.pointTypeName}\nポイント数　　：${value.pointAssignmentPoint}`
            : "なし",
          'アンケート': questionnaire ? `対象者：${target}\n件名　：${questionnaire.subject}\n${answers}` : "なし",
        };
      } else {
        file = {
          ...file,
          '　　　アンケート': questionnaire ? `対象者：${target}\n件名　：${questionnaire.subject}\n${answers}` : "なし",
        };
      }
    }
    file = {
      ...file,
      '　　　掲載終了日': publishEndDate !== undefined ? convertDateToJapaneseString(new Date(publishEndDate)) : '',
      '　　　　　配信者': value?.authorName,
      '　　　　作成日時': `${convertDateToJapaneseString(new Date(value?.createAt))} ${convertTimeToString(new Date(value?.createAt))}`,
    };
  }

  const title = value?.title === "" || value?.title === null
    ? (<span className="text-gray-300">タイトルなし</span>)
    : value?.title

  return (
    <>
      <div className="max-w-full px-3 sm:px-6 lg:px-8">
        <h2 className="text-sm text-gray-900 mb-2">{`配信日時：${convertDateToJapaneseString(new Date(value?.sendDate))} ${convertTimeToString(new Date(value?.sendDate))}`}</h2>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">{title}</h2>

        <div
          ref={containerRef}
          className="relative border shadow overflow-hidden resize-y"
          style={{
            width: "100%",
            minWidth: "450px",
            height: size.height,
          }}
        >
          <div className="h-full w-full overflow-auto p-4 bg-sky-50">
            <InfoBoard col={col} filed={file} children={undefined} flexDisp gapY={"gapY4"} />
          </div>

          <div
            className="absolute bottom-0 right-0 w-5 h-5 cursor-ns-resize"
          >
            <div className="w-full h-full bg-gray-300 opacity-60" />
          </div>
        </div>
      </div>
    </>
  );
};